import React, { createContext, startTransition, useCallback, useContext, useState } from 'react';

// ==================== TYPES ==================================

export type TrackerUserKind = 'mentor' | 'admin' | 'coordinator' | 'salesperson';

export interface MentorSession {
  kind: 'mentor';
  mentorId: string;
  mentorName: string;
  role: string;
  seniorMentorId: string;
  isPlacementCoordinator: boolean;
  // A Career Strategist: the P2P students (csToken signs them in there)
  isCareerStrategist?: boolean;
  csToken?: string;
}

export interface AdminSession {
  kind: 'admin';
  adminPassword: string;
}

export interface CoordinatorSession {
  kind: 'coordinator';
  mentorId: string;
  mentorName: string;
  // The sales meetings' sign-in (server/services/crmAuth.js)
  coordToken: string;
}

export interface SalesPersonSession {
  kind: 'salesperson';
  salesPersonId: string;
  name: string;
  email: string;
  // Sales CRM sign-in and role (server/services/crmAuth.js)
  crmToken: string;
  salesRole: 'Salesperson' | 'Lead Manager' | 'Sales Admin';
  takesLeads: boolean;
}

export type TrackerSession = MentorSession | AdminSession | CoordinatorSession | SalesPersonSession | null;

interface TrackerContextType {
  session: TrackerSession;
  loginAsMentor: (mentorId: string, mentorName: string, role?: string, seniorMentorId?: string, isPlacementCoordinator?: boolean, cs?: { isCareerStrategist?: boolean; csToken?: string }) => void;
  loginAsAdmin: (adminPassword: string) => void;
  loginAsCoordinator: (mentorId: string, mentorName: string, coordToken?: string) => void;
  loginAsSalesPerson: (salesPersonId: string, name: string, email: string, crm?: { crmToken?: string; salesRole?: string; takesLeads?: boolean }) => void;
  logout: () => void;
}

// ==================== CONTEXT ================================

const TrackerContext = createContext<TrackerContextType | undefined>(undefined);

export const TrackerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<TrackerSession>(null);

  const loginAsMentor = useCallback((mentorId: string, mentorName: string, role = 'Standard', seniorMentorId = '', isPlacementCoordinator = false, cs: { isCareerStrategist?: boolean; csToken?: string } = {}) => {
    setSession({ kind: 'mentor', mentorId, mentorName, role, seniorMentorId, isPlacementCoordinator, isCareerStrategist: !!cs.isCareerStrategist, csToken: cs.csToken || '' });
  }, []);

  const loginAsAdmin = useCallback((adminPassword: string) => {
    setSession({ kind: 'admin', adminPassword });
  }, []);

  const loginAsCoordinator = useCallback((mentorId: string, mentorName: string, coordToken = '') => {
    setSession({ kind: 'coordinator', mentorId, mentorName, coordToken });
  }, []);

  const loginAsSalesPerson = useCallback((salesPersonId: string, name: string, email: string, crm: { crmToken?: string; salesRole?: string; takesLeads?: boolean } = {}) => {
    const salesRole = crm.salesRole === 'Lead Manager' || crm.salesRole === 'Sales Admin' ? crm.salesRole : 'Salesperson';
    setSession({ kind: 'salesperson', salesPersonId, name, email, crmToken: crm.crmToken || '', salesRole, takesLeads: salesRole === 'Salesperson' || !!crm.takesLeads });
  }, []);

  // Callers sign out and then navigate to a login page. The router's
  // navigation is a transition, so clearing the session in one as well puts
  // both in the same render: the old page never re-renders with no session
  // (its session hook would throw).
  const logout = useCallback(() => {
    startTransition(() => setSession(null));
  }, []);

  return (
    <TrackerContext.Provider value={{ session, loginAsMentor, loginAsAdmin, loginAsCoordinator, loginAsSalesPerson, logout }}>
      {children}
    </TrackerContext.Provider>
  );
};

export function useTracker(): TrackerContextType {
  const ctx = useContext(TrackerContext);
  if (!ctx) throw new Error('useTracker must be used inside <TrackerProvider>');
  return ctx;
}

export function useMentorSession(): MentorSession {
  const { session } = useTracker();
  if (!session || session.kind !== 'mentor') throw new Error('Not logged in as mentor');
  return session;
}

export function useAdminSession(): AdminSession {
  const { session } = useTracker();
  if (!session || session.kind !== 'admin') throw new Error('Not logged in as admin');
  return session;
}

export function useAdminOrCoordinatorSession(): AdminSession {
  const { session } = useTracker();
  if (session?.kind === 'admin') return session;
  if (session?.kind === 'coordinator') return { kind: 'admin', adminPassword: `COORD:${session.mentorId}` };
  throw new Error('Not logged in as admin or coordinator');
}

export function useCoordinatorSession(): CoordinatorSession {
  const { session } = useTracker();
  if (!session || session.kind !== 'coordinator') throw new Error('Not logged in as a Student Coordinator');
  return session;
}

export function useSalesPersonSession(): SalesPersonSession {
  const { session } = useTracker();
  if (!session || session.kind !== 'salesperson') throw new Error('Not logged in as salesperson');
  return session;
}
